import { Observable, iif, timer, throwError } from 'rxjs'
import { retryWhen, concatMap } from 'rxjs/operators'

// Modified from: https://github.com/alex-okrushko/backoff-rxjs

/** Calculates the actual delay which can be limited by maxInterval */
export const getDelay = (backoffDelay: number, maxInterval: number) => Math.min(backoffDelay, maxInterval)

/** Exponential backoff delay */
export const exponentialBackoffDelay = (iteration: number, initialInterval: number) =>
  Math.pow(2, iteration) * initialInterval

export interface RetryBackoffConfig {
  // Initial interval. It will eventually go as high as maxInterval.
  readonly initialInterval: number
  // Maximum number of retry attempts.
  readonly maxRetries?: number
  // Maximum delay between retries.
  readonly maxInterval?: number
  // Conditional retry.
  shouldRetry?(error: any): boolean
  backoffDelay?(iteration: number, initialInterval: number): number
}

/**
 * Returns an Observable that mirrors the source Observable with the exception
 * of an error. If the source Observable calls error, rather than propagating
 * the error call this method will resubscribe to the source Observable with
 * exponentially increasing interval and up to a maximum of count
 * resubscriptions (if provided). Retrying can be cancelled at any point if
 * shouldRetry returns false.
 */
export const retryBackoff = (config: number | RetryBackoffConfig) => {
  const {
    initialInterval,
    maxRetries = Infinity,
    maxInterval = Infinity,
    shouldRetry = () => true,
    backoffDelay = exponentialBackoffDelay
  } = typeof config === 'number' ? { initialInterval: config } : config
  return <T>(source: Observable<T>) =>
    source.pipe(
      retryWhen<T>(errors =>
        errors.pipe(
          concatMap((error, i) =>
            iif(
              () => i < maxRetries && shouldRetry(error),
              timer(getDelay(backoffDelay(i, initialInterval), maxInterval)),
              throwError(error)
            )
          )
        )
      )
    )
}
